import React, { useState, useEffect } from "react";
import Button from "../../component/shared/Button";
import CustomDropdown from "../../component/shared/CustomDropdown";
import MonthYearPicker from "../../component/shared/MonthYearPicker";
import FileUpload from "../../component/shared/FileUpload";
import TextExtractionBox from "../../component/shared/TextExtractionBox";
import { Api } from "../../network/api";
import { extractTextFromFile } from "../../utils/textExtraction";
import { IconCross } from "../../assets/icons";

const AddNewEssayModal = ({
  isOpen,
  onClose,
  onSubmit,
  existingEssays = [],
}) => {
  const [formData, setFormData] = useState({
    subject: null,
    monthYear: null,
    essayFile: null,
    extractedText: "",
  });

  const [subjects, setSubjects] = useState([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingSubjects, setIsLoadingSubjects] = useState(false);
  const [isNewSubject, setIsNewSubject] = useState(false);
  const [newSubjectName, setNewSubjectName] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchSubjects();
      console.log("hello from the main point");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const fetchSubjects = async () => {
    try {
      setIsLoadingSubjects(true);
      setErrors((prev) => ({ ...prev, subjects: null }));

      const response = await Api.getSubjects();

      if (response?.data?.subjects) {
        const transformedSubjects = response.data.subjects.map((subject) => ({
          value: subject.id.toString(),
          label:
            subject.name || subject.subject_name || `Subject ${subject.id}`,
        }));
        setSubjects(transformedSubjects);
      } else {
        setErrors((prev) => ({
          ...prev,
          subjects: "No subjects data received",
        }));
      }
    } catch (error) {
      console.error("Error fetching subjects:", error);
      setErrors((prev) => ({
        ...prev,
        subjects: "Failed to load subjects. Please try again.",
      }));
    } finally {
      setIsLoadingSubjects(false);
    }
  };

  const resetForm = () => {
    setFormData({
      subject: null,
      monthYear: null,
      essayFile: null,
      extractedText: "",
    });
    setSubjects([]);
    setErrors({});
    setIsLoadingSubjects(false);
    setIsNewSubject(false);
    setNewSubjectName("");
    setIsSuccess(false);
  };

  const validateForm = () => {
    const newErrors = {};

    if (isLoadingSubjects) {
      newErrors.submit = "Please wait for subjects to load";
      setErrors(newErrors);
      return false;
    }


    if (subjects.length === 0 && newSubjectName.length === 0) {
      newErrors.subject = "Please select a subject or enter a new subject name";
    }

    if (!formData?.monthYear) {
      newErrors.monthYear = "Please select month and year";
    }

    if (!formData?.essayFile) {
      newErrors.essayFile = "Please upload an essay file";
    }

    if (formData?.subject && formData?.monthYear) {
      const subjectValue = formData?.subject?.value;
      const existingEssay = existingEssays?.find(
        (essay) =>
          essay.subject === subjectValue &&
          essay.month === formData?.monthYear?.month &&
          essay.year === formData?.monthYear?.year
      );

      if (existingEssay) {
        newErrors.subject =
          "An essay already exists for this subject and month/year combination";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileSelect = (file) => {
    setFormData((prev) => ({ ...prev, essayFile: file }));
    setErrors((prev) => ({ ...prev, essayFile: null }));
    handleTextExtraction(file);
  };

  const handleTextExtraction = async (file) => {
    setIsExtracting(true);
    setExtractionProgress(0);
    setErrors((prev) => ({ ...prev, essayFile: null }));

    try {
      const extractedText = await extractTextFromFile(
        file,
        setExtractionProgress
      );

      setFormData((prev) => ({ ...prev, extractedText }));
      setExtractionProgress(100);
    } catch (error) {
      console.error("Text extraction error:", error);
      setErrors((prev) => ({
        ...prev,
        essayFile: `Text extraction failed: ${error.message}`,
      }));
      setFormData((prev) => ({ ...prev, extractedText: "" }));
    } finally {
      setIsExtracting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("model_answer", formData.essayFile);
      formDataToSend.append("year", formData.monthYear.year);
      formDataToSend.append("month", formData.monthYear.month);

      let subjectId = null;
      if (isNewSubject) {
        formDataToSend.append("new_subject", newSubjectName.trim());
      } else {
        subjectId = formData?.subject?.value;
        if (subjectId && !isNaN(parseInt(subjectId))) {
          formDataToSend.append("subject_id", subjectId);
        }
      }

      const response = await Api.uploadAnswer(formDataToSend);
      console.log("API response:", response);

      const essayData = {
        id: Date.now().toString(),
        subject: subjectId,
        subjectLabel: isNewSubject
          ? newSubjectName.trim()
          : formData?.subject?.label,
        month: formData?.monthYear?.month,
        year: formData?.monthYear?.year,
        fileName: formData?.essayFile?.name,
        fileSize: formData?.essayFile?.size,
        fileType: formData?.essayFile?.type,
        extractedText: formData?.extractedText,
        uploadedAt: new Date().toISOString(),
        status: "active",
        isNewSubject: isNewSubject,
      };
      setIsSuccess(true);
      onSubmit(essayData);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Error submitting essay:", error);
      setErrors({
        submit:
          error.response.data.detail ||
          "Failed to submit essay. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">Add Model Answer</h3>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors duration-200"
              >
                <IconCross />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                {isNewSubject ? (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-[10px] -mt-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      value={newSubjectName}
                      onChange={(e) => {
                        setNewSubjectName(e.target.value);
                        setErrors((prev) => ({ ...prev, newSubject: null }));
                      }}
                      placeholder="Enter a new subject"
                      className="w-full px-3 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      required
                    />
                    {errors?.newSubject && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.newSubject}
                      </p>
                    )}
                  </div>
                ) : (
                  <CustomDropdown
                    label="Subject"
                    options={subjects}
                    value={formData?.subject}
                    onChange={(subject) => {
                      setFormData((prev) => ({ ...prev, subject }));
                      setErrors((prev) => ({ ...prev, subject: null }));
                    }}
                    placeholder={
                      isLoadingSubjects
                        ? "Loading subjects..."
                        : subjects.length === 0
                          ? "No subjects available"
                          : "Select a subject"
                    }
                    error={errors?.subject}
                    disabled={isLoadingSubjects || subjects.length === 0}
                  />
                )}

                <div className="mt-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isNewSubject}
                      onChange={(e) => {
                        setIsNewSubject(e.target.checked);
                        if (e.target.checked) {
                          setFormData((prev) => ({ ...prev, subject: null }));
                          setErrors((prev) => ({
                            ...prev,
                            subject: null,
                            newSubject: null,
                          }));
                        } else {
                          setNewSubjectName("");
                          setErrors((prev) => ({ ...prev, newSubject: null }));
                        }
                      }}
                      className="rounded border-gray-300 text-red-600 shadow-sm focus:border-red-300 focus:ring focus:ring-red-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Add Others
                    </span>
                  </label>
                </div>

                {isLoadingSubjects && (
                  <div className="mt-2 flex items-center text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2" />
                    Loading subjects...
                  </div>
                )}

                {errors?.subjects && (
                  <div className="mt-2">
                    <p className="text-sm text-red-600 mb-2">
                      {errors.subjects}
                    </p>
                    <button
                      type="button"
                      onClick={fetchSubjects}
                      className="text-sm text-red-600 hover:text-red-800 underline"
                    >
                      Retry
                    </button>
                  </div>
                )}

                {!isLoadingSubjects &&
                  subjects.length === 0 &&
                  !errors.subjects && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        No subjects available. Please contact an administrator.
                      </p>
                    </div>
                  )}
              </div>

              <MonthYearPicker
                label="Month & Year"
                value={formData?.monthYear}
                onChange={(monthYear) => {
                  setFormData((prev) => ({ ...prev, monthYear }));
                  setErrors((prev) => ({ ...prev, monthYear: null }));
                }}
                placeholder="Select month and year"
                required
                error={errors.monthYear}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Model Answer File <span className="text-red-500">*</span>
              </label>
              <FileUpload
                onFileSelect={handleFileSelect}
                acceptedTypes={[".pdf"]}
                maxSize={10 * 1024 * 1024}
                disabled={isExtracting}
              />
            </div>

            {formData?.essayFile && (
              <div className="space-y-3">
                <TextExtractionBox
                  extractedText={formData?.extractedText}
                  isExtracting={isExtracting}
                  extractionProgress={extractionProgress}
                  readOnly={true}
                />
                {errors?.essayFile && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center">
                      <svg
                        className="h-5 w-5 text-red-400 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm text-red-800">
                        {errors.essayFile}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {errors?.submit && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}

            {isSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center">
                  <svg
                    className="h-5 w-5 text-green-400 mr-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-sm text-green-800 font-medium">
                    Model answer uploaded successfully!
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                loading={isSubmitting}
                disabled={
                  (!formData?.subject && !isNewSubject) ||
                  !formData?.monthYear ||
                  !formData?.essayFile ||
                  isExtracting ||
                  isLoadingSubjects ||
                  (isNewSubject && newSubjectName.length === 0) ||
                  isSuccess
                }
              >
                {isSubmitting
                  ? "Submitting..."
                  : isSuccess
                    ? "Success!"
                    : "Submit"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddNewEssayModal;
